/**
 * Step titles for Amy's tool calls — keyed on the eve Amy's tool names (see
 * the Amy v2 capability map in the root AGENTS.md). Each entry is
 * [while running, once done], following the AI Elements Tool pattern
 * ("Running …" / "Ran …"). Anything unmapped falls back to the humanized name.
 */
const TOOL_LABELS: Record<string, [running: string, done: string]> = {
  // Reads
  list_campaigns: ["Looking at your campaigns", "Looked at your campaigns"],
  get_campaign: ["Reading the campaign", "Read the campaign"],
  pipeline_snapshot: ["Checking the pipeline", "Checked the pipeline"],
  list_campaign_creators: ["Checking your creators", "Checked your creators"],
  get_creator: ["Reading the creator's profile", "Read the creator's profile"],
  get_creator_thread: ["Reading the conversation", "Read the conversation"],
  get_org_knowledge: ["Consulting the knowledge base", "Consulted the knowledge base"],
  org_usage: ["Checking your plan usage", "Checked your plan usage"],
  list_org_triggers: ["Checking available triggers", "Checked available triggers"],
  list_org_tools: ["Checking connected tools", "Checked connected tools"],
  get_org_tool_schema: ["Checking connected tools", "Checked connected tools"],
  get_connection_status: ["Checking your connections", "Checked your connections"],
  list_saved_fields: ["Checking saved fields", "Checked saved fields"],
  // Writes
  update_campaign: ["Updating the campaign", "Updated the campaign"],
  update_campaign_settings: ["Updating campaign settings", "Updated campaign settings"],
  update_campaign_brief: ["Updating the brief", "Updated the brief"],
  update_campaign_phases: ["Updating the pipeline phases", "Updated the pipeline phases"],
  update_org_knowledge: ["Saving to the knowledge base", "Saved to the knowledge base"],
  update_creator_fields: ["Updating creator details", "Updated creator details"],
  exclude_creator: ["Excluding the creator", "Excluded the creator"],
  set_negotiation_tier: ["Adjusting the negotiation tier", "Adjusted the negotiation tier"],
  clear_creator_error: ["Clearing the error", "Cleared the error"],
  add_creators_to_campaign: ["Adding creators to the campaign", "Added creators to the campaign"],
  set_campaign_mailbox: ["Updating the campaign's mailboxes", "Updated the campaign's mailboxes"],
  set_notification_channel: ["Setting the notification channel", "Set the notification channel"],
  find_creators_for_campaign: ["Searching for creators", "Searched for creators"],
  launch_campaign_outreach: ["Launching outreach", "Launched outreach"],
  send_creator_email: ["Writing the email", "Sent the email"],
  create_campaign: ["Creating the campaign", "Created the campaign"],
  clone_campaign: ["Cloning the campaign", "Cloned the campaign"],
  // Deals & money
  approve_creator: ["Approving the creator", "Approved the creator"],
  reject_creator: ["Sending the rejection", "Sent the rejection"],
  resolve_handoff: ["Resolving the handoff", "Resolved the handoff"],
  resolve_phase_review: ["Recording the review verdict", "Recorded the review verdict"],
  confirm_payout: ["Confirming the payout", "Confirmed the payout"],
  cancel_payout: ["Cancelling the payout", "Cancelled the payout"],
  release_gate: ["Releasing the hold", "Released the hold"],
  // Pausing & connections
  set_creator_autopilot: ["Updating creator autopilot", "Updated creator autopilot"],
  set_campaign_autopilot: ["Updating campaign autopilot", "Updated campaign autopilot"],
  pause_all_automation: ["Pausing automation", "Paused automation"],
  generate_connect_link: ["Preparing a connect link", "Prepared a connect link"],
  execute_org_tool: ["Running the connected tool", "Ran the connected tool"],
};

/** "load_payment_case" -> "load payment case". */
const words = (name: string): string => name.replace(/[_-]+/g, " ").trim();

const capitalize = (s: string): string => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s);

export type ToolPhase = "running" | "done" | "error" | "denied" | "stopped" | "approval";

/** Title for one tool line in the given phase. */
export function toolTitle(name: string, phase: ToolPhase, skill?: boolean): string {
  if (skill) {
    const playbook = words(name);
    return phase === "running" ? `Reading the ${playbook} playbook` : `Read the ${playbook} playbook`;
  }
  const [running, done] = TOOL_LABELS[name] ?? [`Running ${words(name)}`, `Ran ${words(name)}`];
  switch (phase) {
    case "running":
      return running;
    case "done":
      return done;
    case "error":
      return `Couldn't finish: ${lowerFirst(running)}`;
    case "denied":
      return `Declined: ${lowerFirst(running)}`;
    case "stopped":
      return `Stopped: ${lowerFirst(running)}`;
    case "approval":
      return `Waiting for your approval: ${lowerFirst(running)}`;
  }
}

/** Approval prompt for a gated call: "Allow Amy to update the campaign?". */
export function approvalTitle(toolName: string, askedBy?: string): string {
  const [running] = TOOL_LABELS[toolName] ?? [`run ${words(toolName)}`];
  const action = lowerFirst(running.replace(/^(\w+)ing\b/, (_, stem: string) => baseVerb(stem)));
  return `Allow ${askedBy ? subagentLabel(askedBy) : "Amy"} to ${action}?`;
}

const lowerFirst = (s: string): string => (s ? `${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);

/** "Updat" -> "update", "Clear" -> "clear", "Runn" -> "run" (gerund stem back to the verb). */
function baseVerb(stem: string): string {
  const lower = stem.toLowerCase();
  const irregular: Record<string, string> = {
    runn: "run",
    sett: "set",
    writ: "write",
    sav: "save",
    send: "send",
    paus: "pause",
    us: "use",
    prepar: "prepare",
    approv: "approve",
    resolv: "resolve",
    exclud: "exclude",
    releas: "release",
    creat: "create",
    clon: "clone",
    updat: "update",
    launch: "launch",
    consult: "consult",
    cancell: "cancel",
  };
  return irregular[lower] ?? lower;
}

/** Amy's subagents by their eve name. */
const SUBAGENT_LABELS: Record<string, string> = {
  "campaign-builder": "Campaign builder",
  negotiator: "Negotiator",
  outreach: "Outreach agent",
  followup: "Follow-up agent",
  contracting: "Contracting agent",
  "draft-review": "Draft reviewer",
  "script-approval": "Script reviewer",
  posting: "Posting agent",
  payments: "Payments agent",
  "custom-phase": "Custom phase agent",
  automation: "Automation agent",
};

export const subagentLabel = (name: string): string => SUBAGENT_LABELS[name] ?? capitalize(words(name));
